import { OnInit, Directive, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SortBy } from './components/list-sort/list-sort.component';
import { FitList } from './fit-api-client.service';
import { PaginationItem, buildPaginationItems } from './pagination.util';

@Directive()
export abstract class PageableComponent<T> implements OnInit, OnDestroy {
    get list() { return this._list ? this._list.data : []; }
    get page() { return this._page; }
    get totalItems() { return this._list ? this._list.total : 0; }
    get itemsPerPage() { return this._list ? this._list.per_page : 0; }
    get hasPagination() { return this._list && this._list.last_page != 1; }
    get totalPages() { return this._list ? this._list.last_page : 0; }
    get paginationItems(): PaginationItem[] {
        return buildPaginationItems(this.page, this.totalPages);
    }

    public sortBy: SortBy = 'newest';
    public loading = false;
    public searchQuery = '';
    public searchQueryStream = new Subject<string>();
    public markAllItems: boolean = false;
    public markedItems: number[] = [];

    // Abstract method to get module identifier for storage keys
    // Default implementation returns 'default' to avoid breaking existing components
    protected getModuleIdentifier(): string {
        return 'default';
    }

    // Override this method to define custom state properties to persist
    // Return an object with key-value pairs to be saved/restored
    protected getCustomStateProperties(): { [key: string]: any } {
        return {};
    }

    public ngOnInit() {
        // Restore search state from storage
        this.restorePageState();
        
        this.searchQueryStream.pipe(
            debounceTime(500)
        ).subscribe(() => {
            this._page = 1;
            this.savePageState();
            this.load();
        });
    }

    public ngOnDestroy() {
        // Save current search state before component is destroyed
        this.savePageState();
        this.searchQueryStream.complete();
    }

    /**
     * Save current page state to sessionStorage with module-specific keys
     */
    protected savePageState(): void {
        const moduleId = this.getModuleIdentifier();
        const searchKey = `${moduleId}_search_query`;
        const pageKey = `${moduleId}_current_page`;
        const sortKey = `${moduleId}_sort_by`;

        /*
        console.log(`💾 [${moduleId}] Saving page state...`);
        console.log(`💾 [${moduleId}] Internal page (_page): ${this._page}`);
        console.log(`💾 [${moduleId}] Current search: "${this.searchQuery}"`);
        console.log(`💾 [${moduleId}] Current sort: ${this.sortBy}`);
        */

        if (this.searchQuery) {
            sessionStorage.setItem(searchKey, this.searchQuery);
            //console.log(`💾 [${moduleId}] Saved search query: "${this.searchQuery}"`);
        } else {
            sessionStorage.removeItem(searchKey);
            //console.log(`💾 [${moduleId}] Removed search query`);
        }

        // Save sort order
        sessionStorage.setItem(sortKey, this.sortBy);
        //console.log(`💾 [${moduleId}] Saved sort: ${this.sortBy}`);

        // Save custom state properties
        const customState = this.getCustomStateProperties();
        Object.keys(customState).forEach(key => {
            const customKey = `${moduleId}_${key}`;
            const value = customState[key];
            if (value !== null && value !== undefined) {
                sessionStorage.setItem(customKey, JSON.stringify(value));
                //console.log(`💾 [${moduleId}] Saved custom state ${key}: ${JSON.stringify(value)}`);
            } else {
                sessionStorage.removeItem(customKey);
                //console.log(`💾 [${moduleId}] Removed custom state ${key}`);
            }
        });
    }

    /**
     * Restore page state from sessionStorage with module-specific keys
     */
    private restorePageState(): void {
        const moduleId = this.getModuleIdentifier();
        const searchKey = `${moduleId}_search_query`;
        const pageKey = `${moduleId}_current_page`;
        const sortKey = `${moduleId}_sort_by`;

        /*
        console.log(`🔍 [${moduleId}] Restoring page state...`);
        console.log(`🔍 [${moduleId}] Search key: ${searchKey}`);
        console.log(`🔍 [${moduleId}] Page key: ${pageKey}`);
        console.log(`🔍 [${moduleId}] Sort key: ${sortKey}`);
        */

        // Restore search query
        const savedQuery = sessionStorage.getItem(searchKey);
        if (savedQuery) {
            this.searchQuery = savedQuery;
            //console.log(`🔍 [${moduleId}] Restored search query: "${savedQuery}"`);
        } else {
            //console.log(`🔍 [${moduleId}] No saved search query found`);
        }

        // Note: Page restoration removed - will always start from page 1
        //console.log(`🔍 [${moduleId}] Page restoration disabled - starting from page 1`);

        // Restore sort order
        const savedSort = sessionStorage.getItem(sortKey);
        if (savedSort && ['newest', 'oldest', 'name'].includes(savedSort)) {
            this.sortBy = savedSort as SortBy;
            //console.log(`🔍 [${moduleId}] Restored sort: ${savedSort}`);
        } else {
            //console.log(`🔍 [${moduleId}] No saved sort found, using default: ${this.sortBy}`);
        }

        // Restore custom state properties
        const customState = this.getCustomStateProperties();
        Object.keys(customState).forEach(key => {
            const customKey = `${moduleId}_${key}`;
            const savedValue = sessionStorage.getItem(customKey);
            if (savedValue) {
                try {
                    const parsedValue = JSON.parse(savedValue);
                    (this as any)[key] = parsedValue;
                    //console.log(`🔍 [${moduleId}] Restored custom state ${key}: ${JSON.stringify(parsedValue)}`);
                } catch (e) {
                    //console.log(`🔍 [${moduleId}] Failed to parse custom state ${key}: ${savedValue}`);
                }
            }
        });
    }

    /**
     * Clear page state from storage
     */
    public clearPageState(): void {
        const moduleId = this.getModuleIdentifier();
        const searchKey = `${moduleId}_search_query`;
        const pageKey = `${moduleId}_current_page`;
        const sortKey = `${moduleId}_sort_by`;

        sessionStorage.removeItem(searchKey);
        sessionStorage.removeItem(pageKey);
        sessionStorage.removeItem(sortKey);

        // Clear custom state properties
        const customState = this.getCustomStateProperties();
        Object.keys(customState).forEach(key => {
            const customKey = `${moduleId}_${key}`;
            sessionStorage.removeItem(customKey);
        });
    }

    /**
     * Debug method to inspect current storage state
     */
    public debugStorageState(): void {
        const moduleId = this.getModuleIdentifier();
        const searchKey = `${moduleId}_search_query`;
        const pageKey = `${moduleId}_current_page`;
        const sortKey = `${moduleId}_sort_by`;

        console.log(`🔍 [${moduleId}] === STORAGE DEBUG ===`);
        console.log(`🔍 [${moduleId}] Search (${searchKey}):`, sessionStorage.getItem(searchKey));
        console.log(`🔍 [${moduleId}] Page (${pageKey}):`, sessionStorage.getItem(pageKey));
        console.log(`🔍 [${moduleId}] Sort (${sortKey}):`, sessionStorage.getItem(sortKey));
        
        // Debug custom state properties
        const customState = this.getCustomStateProperties();
        Object.keys(customState).forEach(key => {
            const customKey = `${moduleId}_${key}`;
            console.log(`🔍 [${moduleId}] Custom ${key} (${customKey}):`, sessionStorage.getItem(customKey));
        });
        
        console.log(`🔍 [${moduleId}] Current component state:`);
        console.log(`🔍 [${moduleId}]   - searchQuery: "${this.searchQuery}"`);
        console.log(`🔍 [${moduleId}]   - _page: ${this._page}`);
        console.log(`🔍 [${moduleId}]   - sortBy: ${this.sortBy}`);
        
        // Debug custom state values
        Object.keys(customState).forEach(key => {
            console.log(`🔍 [${moduleId}]   - ${key}: ${JSON.stringify((this as any)[key])}`);
        });
        
        console.log(`🔍 [${moduleId}] === END DEBUG ===`);
    }

    public loadQuery() {
        if (sessionStorage.getItem('load-query')) {
            if (sessionStorage.getItem('query')){
                this.searchQuery = sessionStorage.getItem('query');
                sessionStorage.removeItem('query');
            }

            if (sessionStorage.getItem('page')){
                this._page = parseInt(sessionStorage.getItem('page'), 10);
                sessionStorage.removeItem('page');
            }

            sessionStorage.removeItem('load-query');
        }
    }

    public onPageChanged($event: { page: number, itemsPerPage: number }) {
        this._page = $event.page;
        this.load();
    }

    public clearSearch() {
        this.searchQuery = '';
        this._page = 1;
        this.clearPageState();
        this.load();
    }

    public onSort(sortBy: SortBy) {
        if (this.sortBy === sortBy) {
            return;
        }
        this.sortBy = sortBy;
        this.load();
    }

    public isItemMarked(id: number): boolean {
        return this.markedItems.includes(id);
    }

    public toggleItemMarked(id: number) {
        this.isItemMarked(id) 
            ? this.markedItems = this.markedItems.filter(item => item !== id) 
            : this.markedItems.push(id);

        this.updateMarkAllItemsState();
    }

    public toggleAllMarked() {
        this.markedItems = this.markAllItems 
            ? [...new Set(this.markedItems.concat(this.list.map(item => item.id)))] //Set removes duplicates
            : this.markedItems.filter(item => !this.list.some(meal => item === meal.id));
    }

    public updateMarkAllItemsState() { //this need to be overriden in list component
        this.markAllItems = this.list.every(item => this.markedItems.includes(item.id));
    }

    public abstract load();
    protected _list: FitList<any>;
    protected _page = 1;
}
